import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Class, Enrollment } from '@/types/classTypes';
import { tables } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { UsersRound } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { Clipboard, Users } from "lucide-react";
import WaitlistManager from '../WaitlistManager';

interface InfoTabProps {
  classData: Class;
  enrollments: Enrollment[];
  onWaitlistManage?: () => void;
}

export const InfoTab: React.FC<InfoTabProps> = ({ classData, enrollments, onWaitlistManage }) => {
  const { organizationId } = useAuth();
  
  const activeEnrollments = enrollments.filter(e => e.status === 'active');
  const waitlistCount = enrollments.filter(e => 
    e.status === 'waitlisted'
  ).length;
  
  const isAtCapacity = classData.max_students && 
    activeEnrollments.length >= classData.max_students;
  
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount / 100); // Convert cents to dollars
  };

  // Fetch location details if a location_id is available
  const { data: location, isLoading: locationLoading } = useQuery({
    queryKey: ['location', classData.location_id],
    queryFn: async () => {
      if (!classData.location_id) return null;
      
      // Handle special location types
      if (classData.location_id === 'virtual') {
        return { name: 'Virtual Class', type: 'virtual' };
      }
      if (classData.location_id === 'offsite') {
        return { name: 'Off-site Location', type: 'external' };
      }
      
      // Fetch from database
      const { data, error } = await tables.locations()
        .select('*')
        .eq('id', classData.location_id)
        .maybeSingle();
        
      if (error) throw error;
      return data;
    },
    enabled: !!classData.location_id && !!organizationId,
  });

  const getLocationDisplay = () => {
    if (locationLoading) return 'Loading location...';
    if (location) return location.name;
    return 'No location specified';
  };
  
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Class Details</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="space-y-4">
              <div className="flex flex-col">
                <dt className="text-sm font-medium text-gray-500">Description</dt>
                <dd className="mt-1 text-sm">{classData.description || "No description provided"}</dd>
              </div>
              
              <div className="flex flex-col">
                <dt className="text-sm font-medium text-gray-500">Class Type</dt>
                <dd className="mt-1 text-sm">
                  {classData.is_event ? (
                    <Badge variant="secondary">Event</Badge>
                  ) : (
                    <Badge variant="default">Class</Badge>
                  )}
                </dd>
              </div>
              
              <div className="flex flex-col">
                <dt className="text-sm font-medium text-gray-500">Status</dt>
                <dd className="mt-1 text-sm">
                  {classData.status === 'active' ? (
                    <Badge className="bg-green-100 text-green-800" variant="outline">Active</Badge>
                  ) : (
                    <Badge className="bg-gray-100 text-gray-800" variant="outline">Inactive</Badge>
                  )}
                </dd>
              </div>
              
              <div className="flex flex-col">
                <dt className="text-sm font-medium text-gray-500">Category</dt>
                <dd className="mt-1 text-sm">{classData.category || "Uncategorized"}</dd>
              </div>
              <div className="flex flex-col">
                <dt className="text-sm font-medium text-gray-500">Level</dt>
                <dd className="mt-1 text-sm">{classData.level || "Not specified"}</dd>
              </div>
              <div className="flex flex-col">
                <dt className="text-sm font-medium text-gray-500">Dress Code</dt>
                <dd className="mt-1 text-sm">{classData.dress_code || "Not specified"}</dd>
              </div>
              <div className="flex flex-col">
                <dt className="text-sm font-medium text-gray-500">Trial Available</dt>
                <dd className="mt-1 text-sm">{classData.trial_available ? "Yes" : "No"}</dd>
              </div>
              <div className="flex flex-col">
                <dt className="text-sm font-medium text-gray-500">Active</dt>
                <dd className="mt-1 text-sm">{classData.is_active ? "Yes" : "No"}</dd>
              </div>
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Enrollment & Pricing</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="space-y-4">
              <div className="flex flex-col">
                <dt className="text-sm font-medium text-gray-500">Students Enrolled</dt>
                <dd className="mt-1 text-sm flex items-center">
                  <Users className="h-4 w-4 mr-1.5 text-muted-foreground" />
                  {activeEnrollments.length} of {classData.max_students || "∞"}
                  {isAtCapacity && (
                    <Badge className="ml-2 bg-amber-100 text-amber-800" variant="outline">
                      At Capacity
                    </Badge>
                  )}
                </dd>
              </div>
              
              <div className="flex flex-col">
                <dt className="text-sm font-medium text-gray-500">Waitlist</dt>
                <dd className="mt-1 text-sm flex items-center">
                  <Clipboard className="h-4 w-4 mr-1.5 text-muted-foreground" />
                  {waitlistCount} students
                  {waitlistCount > 0 && onWaitlistManage && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="ml-2 h-7" 
                      onClick={onWaitlistManage}
                    >
                      Manage
                    </Button>
                  )}
                </dd>
              </div>
              
              <div className="flex flex-col">
                <dt className="text-sm font-medium text-gray-500">Tuition</dt>
                <dd className="mt-1 text-sm">
                  {classData.price_cents ? formatCurrency(classData.price_cents) : "Free"}
                </dd>
              </div>
              
              <div className="flex flex-col">
                <dt className="text-sm font-medium text-gray-500">Created</dt>
                <dd className="mt-1 text-sm">
                  {classData.created_at ? format(new Date(classData.created_at), 'MMMM d, yyyy') : "N/A"}
                </dd>
              </div>
            </dl>
          </CardContent>
        </Card>
      </div>

      {/* Waitlist Manager Section */}
      {(isAtCapacity || waitlistCount > 0) && (
        <WaitlistManager
          classId={classData.id}
          organizationId={classData.organization_id}
          className={classData.name}
          hasAvailableSpots={!isAtCapacity}
          maxCapacity={classData.max_students || 0}
          currentEnrollment={activeEnrollments.length}
        />
      )}
    </div>
  );
};

